import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { SimilarStructure } from '../../../../entry/structure-data.models';
import { ConfigService } from '../../../../config.service';
import { CommonService } from '../../../../common.service';
import { GoogleAnalyticsService } from '../../../../google-analytics.service';
import { HttpResponse } from '@angular/common/http';
import { CheckboxService } from '../checkbox.service';
import { Subscription } from 'rxjs';
import { FoldseekService } from '../../../../foldseek-table/foldseek.service';
import { Sort } from '@angular/material/sort';

@Component({
  selector: 'mb-allbacteria-tab',
  templateUrl: './mb-allbacteria-tab.component.html',
  styleUrls: ['./mb-allbacteria-tab.component.css', '../mb-foldseek-table.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class MbAllbacteriaTabComponent implements OnInit, OnDestroy {
  @Input() accession: string;
  @Input() afdb_accession: string;
  @Output() toggleClickedEvent: EventEmitter<Object> = new EventEmitter();

  displayedColumnsATBCStructure: string[] = ['expand', 'alighin3D', 'afdbAccessions', 'speciesNames', 'sequenceLength', 'evalue', 'sequenceIdentity', 'averagePlddt'];
  atbcStructureDataSource = new MatTableDataSource<SimilarStructure>();
  isATBCLoading = false;
  atbcZeroResultsMsg = "";
  errorATBC = "";
  isDatabaseErrorATBC = false;
  startATBC = "0";
  recordsATBC = "10";
  currentATBCPage = 0;
  sort_direction_ATBC = 'asc';
  sort_column_ATBC = 'evalue';
  filteritemATBC = "";
  species_name_filter_ATBC = "";
  expandedElementsATBC = [];
  dataLoaded = false;

  checkboxState: { [key: string]: boolean } = {};
  disabledState: { [key: string]: boolean } = {};
  private subscription: Subscription;
  apiData = {};

  constructor(
    private commonService: CommonService,
    private configService: ConfigService,
    public gaService: GoogleAnalyticsService,
    private changeDetectorRef: ChangeDetectorRef,
    private checkboxService: CheckboxService,
    public foldseekService: FoldseekService
  ) {}

  ngOnInit(): void {
    this.subscription = this.checkboxService.checkboxState$.subscribe(state => {
      this.checkboxState = state;
      this.changeDetectorRef.detectChanges();
    });
    this.checkboxService.disabledState$.subscribe(state => {
      this.disabledState = state;
    });
    this.dataLoaded = true;
    this.loadData(this.recordsATBC, this.startATBC, this.sort_direction_ATBC, this.sort_column_ATBC, this.species_name_filter_ATBC);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  clickToggleSuperpose(event, accession) {
    this.toggleClickedEvent.emit({ event, accession, apidata: this.apiData[accession] });
  }

  toggleRowATBC(element) {
    const ele = this.expandedElementsATBC.find(e => e.afdbAccessions === element.afdbAccessions);
    if (ele) {
      this.expandedElementsATBC = this.expandedElementsATBC.filter(el => el.afdbAccessions !== element.afdbAccessions);
    } else {
      this.expandedElementsATBC.push(element);
    }
  }

  isExpandedATBC(element): boolean {
    return !!this.expandedElementsATBC.find(e => e.afdbAccessions === element.afdbAccessions);
  }

  isExpansionDetailRowATBC = (i: number, row) => this.isExpandedATBC(row);

  checkAndLoadData() {
    if (this.dataLoaded) return;
    this.dataLoaded = true;
    this.loadData(this.recordsATBC, this.startATBC, this.sort_direction_ATBC, this.sort_column_ATBC, this.species_name_filter_ATBC);
  }

  loadData(records = "", start = "", sort_direction = 'asc', sort_column = 'evalue', species_name_filter = "") {
    const auth = this.configService.getConfig().apiKey ? `&key=${this.configService.getConfig().apiKey}` : "";
    const configUrl = this.configService.getConfig().searchApiUrl || "https://test.alphafold.ebi.ac.uk/api/search";
    let apiUrl = `${configUrl}?q=${this.accession}&type=structure&foldseek_database=atbc`;
    if (records) apiUrl += `&rows=${records}`;
    if (start) apiUrl += `&start=${start}`;
    if (sort_column && sort_direction) {
      if (sort_column === "evalue") apiUrl += `&sort=${sort_column}+${sort_direction}`;
      if (sort_column === "sequenceIdentity") apiUrl += `&sort=pident+${sort_direction}`;
    }
    if (species_name_filter) apiUrl += `&fq=organismScientificName%3A*${species_name_filter.trim()}*`;

    this.isATBCLoading = true;
    this.commonService.getApiDatawithStatus(apiUrl + auth).subscribe(async (response: HttpResponse<any>) => {
      const code = await Number(response.status);
      if (code === 202) {
        if (response.body.message && (response.body.message.startsWith("Foldseek search job is currently running") || response.body.message.startsWith("Initiating new Foldseek search"))) {
          setTimeout(() => {
            if (this.isATBCLoading) this.loadData(records, start, sort_direction, sort_column, species_name_filter);
          }, 15000);
        }
      }
      if (code === 200) {
        if (response.body.numFound !== 0) {
          const data = response.body.docs;
          this.foldseekService.updateTotalAllBacteria(response.body.numFound);
          this.errorATBC = "";
          this.atbcZeroResultsMsg = "";
          const similarStructureData = this.transformATBCStructureData(data);
          this.atbcStructureDataSource = new MatTableDataSource<SimilarStructure>(similarStructureData);
        } else {
          this.atbcZeroResultsMsg = "No matching structures were found in AllTheBacteria.";
        }
        this.isATBCLoading = false;
        this.changeDetectorRef.detectChanges();
      }
    },
    (error) => {
      const code = error.status;
      this.isATBCLoading = false;
      if (code === 404) {
        if (error.error.message === "The search was completed successfully, but no matching results were found") {
          this.atbcZeroResultsMsg = "No matching structures were found in AllTheBacteria.";
        } else if (species_name_filter !== "") {
          this.errorATBC = "No species match your search";
          this.atbcZeroResultsMsg = "";
        }
      }
      if (code === 422 || code === 302 || code === 500 || code === 0 || code === 429) {
        this.isDatabaseErrorATBC = true;
      }
      this.changeDetectorRef.detectChanges();
    });
  }

  handlePageEvent(e) {
    const start = (e.pageIndex * e.pageSize).toString();
    this.startATBC = start;
    this.recordsATBC = e.pageSize.toString();
    this.currentATBCPage = e.pageIndex;
    this.loadData(this.recordsATBC, start, this.sort_direction_ATBC, this.sort_column_ATBC, this.filteritemATBC);
    this.gaService.eventEmitter('foldseek_table_action', 'FS_similar_structures', 'click', 'foldseek_paginate', 'Clicks on the paginations in the Foldseek results table');
  }

  sortTable(sortState: Sort) {
    const directions = { "ASC": "DESC", "DESC": "ASC" };
    this.sort_direction_ATBC = directions[sortState.direction.toUpperCase()];
    this.sort_column_ATBC = sortState.active;
    this.loadData(this.recordsATBC, this.startATBC, this.sort_direction_ATBC, this.sort_column_ATBC, this.filteritemATBC);
  }

  filterTable(filterby) {
    this.currentATBCPage = 0;
    this.filteritemATBC = filterby;
    this.startATBC = "0";
    this.species_name_filter_ATBC = filterby;
    this.loadData(this.recordsATBC, "0", this.sort_direction_ATBC, this.sort_column_ATBC, filterby);
    this.gaService.eventEmitter('foldseek_table_action', 'FS_similar_structures', 'click', 'foldseek_taxfilter', 'Clicks on the "Taxonomy filter" input box on the Foldseek results table');
  }

  clearFilter() {
    this.currentATBCPage = 0;
    this.filteritemATBC = "";
    this.species_name_filter_ATBC = "";
    this.loadData(this.recordsATBC, "0", this.sort_direction_ATBC, this.sort_column_ATBC, "");
    this.changeDetectorRef.detectChanges();
  }

  getTitle(accession, reviewedStatus, referenceLabel) {
    if (!accession) return "Unavailable";
    return `${accession}*${referenceLabel}*${reviewedStatus}`;
  }

  getLink(database, target) {
    if (database === "pdb") {
      const [entry, , protein] = target.split("_");
      return protein ?
        `${this.configService.getConfig().pdbeBaseApiUrl}/entry/pdb/${entry}/protein/${protein}` :
        `${this.configService.getConfig().pdbeBaseApiUrl}/entry/pdb/${entry}`;
    }
    return `/entry/${target}`;
  }

  transformATBCStructureData(data) {
    const similarClusterData = [];
    if (data) {
      const databaseInfo = {};
      for (let i = 0; i < data.length; i++) {
        similarClusterData.push({
          "refLinkId": this.getLink(data[i].database, data[i].target),
          "afdbAccessions": this.getTitle(data[i].target, data[i].isReviewed, data[i].isReferenceProteome),
          "targetToShow": data[i].modelEntityId,
          "uniprotDescriptions": data[i].uniprotDescription,
          "speciesNames": data[i].organismScientificName ? data[i].organismScientificName : "",
          "sequenceIdentity": data[i].pident ? data[i].pident : "N/A",
          "sequenceLength": data[i].uniprotEnd ? `1-${data[i].uniprotEnd}` : "N/A",
          "evalue": (data[i].evalue != null && data[i].evalue !== "") ? Number(data[i].evalue).toExponential(2) : "N/A",
          "averagePlddt": data[i].globalMetricValue ? data[i].globalMetricValue : "N/A",
          "alighin3D": false,
          "expanded": false,
          "hsps": {
            "current_entry": this.afdb_accession,
            "accession": data[i].target,
            "query_sequence": data[i].qaln,
            "match_sequence": data[i].qaln,
            "match_accession": data[i].taln,
            "qstart": data[i].qstart,
            "qend": data[i].qend,
            "tstart": data[i].tstart,
            "tend": data[i].tend,
            "sequence_stats": [],
            "targetToShow": data[i].entryId,
          }
        });

        data[i].database = "afdb50";
        const foldseekWithDesc = {
          ...data[i],
          targetToShow: data[i].entryId,
          desc: data[i].uniprotDescription,
          refLinkId: this.getLink(data[i].database, data[i].target),
        };
        foldseekWithDesc.tstructure = {
          "url": `https://alphafold.ebi.ac.uk/files/${data[i].modelEntityId}-model_v1.bcif`,
          "format": 'cif',
          "binary": true,
        };
        databaseInfo[data[i].target] = foldseekWithDesc;
      }
      this.apiData = { ...this.apiData, ...databaseInfo };
    }
    return similarClusterData;
  }
}
